// External libraries
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

// Zocdoc deps
import { EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';

// BWA
import { getValidatorForPatientInfo as getValidator } from '../../utils/CreateAccountValidation';
import {
    componentNames,
    elementNames,
    elementMetricNames,
    errorNames,
} from './constants';

const areAnyElementsInvalid = ({ elements, invalidFields, touched }) =>
    elements.some(element => invalidFields[element] && touched[element]);

const wereAllElementsTouched = ({ elements, touched }) =>
    elements.every(element => touched[element]);

const isRealCalendarDate = (year, month, day) => {
    /* If the day of the month is not a valid day of that month,
     * the dayjs date will overflow into the next month, so that
     * for example February 31 will end up early in March,
     * so we can check that the month didn't change to make sure
     * that the date is valid. See https://stackoverflow.com/a/5812341
     * */
    var d = dayjs(`${year}-${month}-${day}`);

    const isValid = d && parseInt(d.format('MM'), 10) === parseInt(month, 10);

    return isValid;
};

export const usePatientInfoForm = ({ onSubmitSuccess, sendMetric }) => {
    const [focused, setFocused] = useState(null);

    const [touched, setTouched] = useState({
        [elementNames.firstName]: false,
        [elementNames.lastName]: false,
        [elementNames.birthDay]: false,
        [elementNames.birthMonth]: false,
        [elementNames.birthYear]: false,
        [elementNames.sex]: false,
    });

    const [values, setValues] = useState({
        [elementNames.firstName]: '',
        [elementNames.lastName]: '',
        [elementNames.birthDay]: '',
        [elementNames.birthMonth]: '',
        [elementNames.birthYear]: '',
        [elementNames.sex]: '',
    });

    const [invalidFields, setInvalidFields] = useState({
        [elementNames.firstName]: true,
        [elementNames.lastName]: true,
        [elementNames.birthDay]: true,
        [elementNames.birthMonth]: true,
        [elementNames.birthYear]: true,
        [elementNames.sex]: true,
    });

    const [serverBirthDateError, setServerBirthDateError] = useState(false);

    const handleBlur = useCallback(
        e => {
            if (e) {
                // Until React 17 we need this to be able to access the e.target.name,
                // see https://reactjs.org/docs/legacy-event-pooling.html
                e.persist();

                if (e.target.value) {
                    const element = elementMetricNames[e.target.name];
                    const component = componentNames[e.target.name];
                    sendMetric({
                        action: `Typed ${element.toLowerCase()}`,
                        component,
                        element,
                        initiator: EVENT_INITIATORS.USER,
                        interactionType: INTERACTION_TYPES.TYPE,
                    });
                }

                /* touched is an object React probably compares by reference,
                 * so update it only if it really changed to avoid rerenders
                 */
                const updatedTouches = touched =>
                    touched[e.target.name]
                        ? touched
                        : { ...touched, [e.target.name]: true };
                setTouched(updatedTouches);
            }

            setFocused(null);
        },
        [sendMetric]
    );

    const handleFocus = useCallback(e => {
        if (e) {
            // Until React 17 we need this to be able to access the e.target.name,
            // see https://reactjs.org/docs/legacy-event-pooling.html
            e.persist();
            setFocused(e.target.name);
        }
    }, []);

    const handleChange = useCallback(
        async e => {
            if (e) {
                // Until React 17 we need this to be able to access the e.target.name,
                // see https://reactjs.org/docs/legacy-event-pooling.html
                e.persist();

                const { name, value } = e.target;
                /* values is an object React probably compares by reference,
                 * so update it only if it really changed to avoid rerenders
                 */

                setValues(values =>
                    values[name] === value
                        ? values
                        : { ...values, [name]: value }
                );
                const validate = getValidator(name);
                const isInvalid = !(await validate(value));

                /* invalidFields is an object React probably compares by reference,
                 * so update it only if it really changed to avoid rerenders
                 */
                setInvalidFields(invalidFields =>
                    invalidFields[name] === isInvalid
                        ? invalidFields
                        : {
                              ...invalidFields,
                              [name]: isInvalid,
                          }
                );
            }
        },
        [setValues]
    );

    // The FEC Radio button has a weird API where it passes
    // the value and not the event to the callback so we have
    // to write a unique handler for it.
    const handleChangeSex = useCallback(
        async value => {
            if (value) {
                const element = elementMetricNames[elementNames.sex];
                const component = componentNames[elementNames.sex];
                sendMetric({
                    action: `Selected ${element.toLowerCase()}`,
                    component,
                    element,
                    initiator: EVENT_INITIATORS.USER,
                    interactionType: INTERACTION_TYPES.SELECT,
                });
            }

            const updatedTouches = touched =>
                touched[elementNames.sex]
                    ? touched
                    : { ...touched, [elementNames.sex]: true };
            setTouched(updatedTouches);

            const updateValues = values => ({
                ...values,
                [elementNames.sex]: value,
            });
            setValues(updateValues);

            /* Create Account Form named this differently. Neither of the names
             * is really accurate because we didn't do a good enough job of
             * clarifying what we're even collecting here and why and showing
             * that we understand the difference between sex and gender
             */
            const validator = getValidator('gender');
            const isInvalid = !(await validator(value));

            const updatedInvalidFields = invalidFields =>
                invalidFields[elementNames.sex] === isInvalid
                    ? invalidFields
                    : { ...invalidFields, [elementNames.sex]: isInvalid };
            setInvalidFields(updatedInvalidFields);
        },
        [sendMetric]
    );

    const nameError = useMemo(() => {
        const nameFields = [elementNames.firstName, elementNames.lastName];
        const hasError = areAnyElementsInvalid({
            elements: nameFields,
            invalidFields,
            touched,
        });

        return hasError;
    }, [invalidFields, touched]);

    const birthDateError = useMemo(() => {
        if (serverBirthDateError) {
            return true;
        }

        const birthDateFields = [
            elementNames.birthDay,
            elementNames.birthMonth,
            elementNames.birthYear,
        ];

        const isAnyBirthDateFieldInvalid = areAnyElementsInvalid({
            elements: birthDateFields,
            invalidFields,
            touched,
        });
        if (isAnyBirthDateFieldInvalid) {
            return true;
        } else {
            const wereAllBirthDateFieldsTouched = wereAllElementsTouched({
                elements: birthDateFields,
                touched,
            });

            if (wereAllBirthDateFieldsTouched) {
                const isBogusDate = !isRealCalendarDate(
                    values[elementNames.birthYear],
                    values[elementNames.birthMonth],
                    values[elementNames.birthDay]
                );

                return isBogusDate;
            } else {
                return false;
            }
        }
    }, [invalidFields, serverBirthDateError, touched, values]);

    const tooYoungError = useMemo(() => {
        if (
            invalidFields[elementNames.birthDay] ||
            invalidFields[elementNames.birthMonth] ||
            /* Don't just check for invalid year because validator
             * marks too young year as invalid
             */
            !values[elementNames.birthYear]
        ) {
            return false;
        }

        const isTooYoung = dayjs(
            `${values[elementNames.birthYear]}-${
                values[elementNames.birthMonth]
            }-${values[elementNames.birthDay]}`
        ).isAfter(dayjs().subtract(18, 'year'));

        return !birthDateError && isTooYoung;
    }, [birthDateError, invalidFields, values]);

    const sexError = useMemo(() => {
        return !values[elementNames.sex] && touched[elementNames.sex];
    }, [touched, values]);

    const errors = {
        [errorNames.nameError]: nameError,
        [errorNames.birthDateError]: birthDateError,
        [errorNames.tooYoungError]: tooYoungError,
        [errorNames.sexError]: sexError,
    };

    useEffect(() => {
        if (nameError) {
            sendMetric({
                action: 'View name error',
                component: 'Name',
                element: 'Error',
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
        }
    }, [nameError, sendMetric]);

    useEffect(() => {
        if (birthDateError) {
            sendMetric({
                action: 'View birth date error',
                component: 'Birth date',
                element: 'Error',
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
        }
    }, [birthDateError, sendMetric]);

    useEffect(() => {
        if (sexError) {
            sendMetric({
                action: 'View sex required error',
                component: 'Sex',
                element: 'Error',
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
        }
    }, [sexError, sendMetric]);

    useEffect(() => {
        if (tooYoungError) {
            sendMetric({
                action: 'View too young error',
                component: 'Birth date',
                element: 'Error',
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
        }
    }, [tooYoungError, sendMetric]);

    const handleClickSubmit = useCallback(() => {
        sendMetric({
            action: 'Clicked submit',
            component: 'Submit button',
            element: 'Submit button',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });

        setTouched({
            [elementNames.firstName]: true,
            [elementNames.lastName]: true,
            [elementNames.birthDay]: true,
            [elementNames.birthMonth]: true,
            [elementNames.birthYear]: true,
            [elementNames.sex]: true,
        });

        const formHasInvalidFields = Object.entries(invalidFields)
            .map(entry => {
                // eslint-disable-next-line no-unused-vars
                const [_, isInvalid] = entry;
                return isInvalid;
            })
            .some(Boolean);

        const formHasErrors = Object.entries(errors)
            .map(entry => {
                // eslint-disable-next-line no-unused-vars
                const [_, hasError] = entry;
                return hasError;
            })
            .some(Boolean);

        if (!formHasInvalidFields && !formHasErrors) {
            /* These errors can be set by request handler if necessary
             * on the next account creation request, but the current
             * value if any becomes irrelevant once we leave this page
             * because it's from the context of the last failed request
             */
            setServerBirthDateError(false);

            /* Moving on to the next page */
            onSubmitSuccess();
        }
    }, [errors, invalidFields, onSubmitSuccess, sendMetric]);

    const highlightedFields = useMemo(() => {
        return Object.entries(invalidFields).reduce(
            (highlightedFields, invalidFieldsEntry) => {
                const [name, isInvalid] = invalidFieldsEntry;
                const isHighlighted =
                    isInvalid && touched[name] && focused !== name;
                return { ...highlightedFields, [name]: isHighlighted };
            },
            {}
        );
    }, [focused, invalidFields, touched]);

    return {
        errors,
        handleBlur,
        handleChange,
        handleChangeSex,
        handleClickSubmit,
        handleFocus,
        highlightedFields,
        setInvalidFields,
        setServerBirthDateError,
        values,
    };
};

export default usePatientInfoForm;
